'use client';

import React from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import { FilterSelect, ResponsiveTooltip } from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import { SearchInputIc, ResetFiltersIc } from '../../../../components/icons';
import {
  getStatusOptions,
  getPeriodOptions,
  getLimitOptions,
  getCampaignTypeOptions,
} from '../constants';
import CustomHeroButton from '../../../../components/button';
import { FiltersProps } from '../types';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  isLoading,
  hasActiveFilters,
  onSearchChange,
  onFilterChange,
  onResetFilters,
  onNewCost,
}) => {
  const { t } = useTranslations();

  return (
    <div className="flex md:flex-row flex-col md:items-center md:justify-between w-full md:gap-0 gap-4">
      <div className="flex items-center gap-4 md:flex-wrap max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        {/* Search input */}
        <Input
          type="text"
          placeholder={t('partner.revshare.costs.searchPlaceholder')}
          size="sm"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm text-auth-ink placeholder:text-neutral-700',
            inputWrapper:
              'rounded-2 h-9 w-[17.0625rem] bg-auth-surface border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
            innerWrapper: 'bg-transparent',
          }}
        />

        {/* Campaign type filter */}
        {/* <FilterSelect
          placeholder={t('partner.revshare.costs.filters.campaign')}
          options={getCampaignTypeOptions(t)}
          selectedKey={filters.campaign}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('campaign', value)}
          startContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1.66667" y="1.66667" width="16.6667" height="16.6667" rx="5" stroke="#A8A8B0" strokeWidth="1.5"/>
              <path d="M5.83333 15V7.5" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 15V5" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14.1667 15V10.8333" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          autoWidth={true}
        /> */}

        {/* Status filter */}
        <FilterSelect
          placeholder={t('partner.revshare.costs.filters.status')}
          options={getStatusOptions(t)}
          selectedKey={filters.status}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('status', value)}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.8333 1.66667L3.33333 11.6667H10L9.16667 18.3333L16.6667 8.33333H10L10.8333 1.66667Z"
                stroke="#A8A8B0"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          autoWidth={true}
        />

        {/* Period filter */}
        <FilterSelect
          placeholder={t('partner.revshare.costs.filters.period')}
          options={getPeriodOptions(t)}
          selectedKey={filters.period}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('period', value)}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 5V10L13.3333 11.6667M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66666 14.6024 1.66666 10C1.66666 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z"
                stroke="#A8A8B0"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          autoWidth={true}
        />

        {/* Limit filter */}
        {/* <FilterSelect
          placeholder={t('partner.revshare.costs.filters.limit')}
          options={getLimitOptions(t)}
          selectedKey={filters.limit}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('limit', value)}
          startContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.83333 1.66667H14.1667M6.66667 18.3333H13.3333M5.83333 1.66667L6.66667 6.66667L10 10L6.66667 13.3333L5.83333 18.3333H14.1667L13.3333 13.3333L10 10L13.3333 6.66667L14.1667 1.66667H5.83333Z" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          autoWidth={true}
        /> */}

        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {/* Reset filters button */}
        {hasActiveFilters && (
          <Tooltip content={t('partner.revshare.offers.filters.resetTooltip')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>

      {/* New Cost button */}
      {onNewCost && (
        <CustomHeroButton
          title={t('partner.revshare.costs.newCost')}
          onPress={onNewCost}
          variant="primary"
        />
      )}
    </div>
  );
};

export default FiltersSection;
